import React, { memo, useState } from 'react';
import {
  View,
  TextInput,
  Text,
  ActivityIndicator,
  UIManager,
} from 'react-native';
import classes from '../../styles/globalClasses';
import gs from '../../styles/globalStyles';
import ls from './style';
import FadeInAnimation from '../FadinAnimation/FadinAnimation';
import Typography from '../typography/Typography';
import {
  ColorTokens,
  ColorTokensMap,
  FontFamilyTokens,
  FontSizeTokens,
} from '@voltmoney/schema';
import { useOTPVerification } from './useOTP';
import { useTranslation } from 'react-i18next';
import ThemedTouchableOpacity from '../../theme/Components/ThemedTouchableOpacity'
import { checkColor } from '@app/utils';
import ThemeProvider from '@voltmoney/theme';

// Define the props for the OTPVerification component
interface OTPVerificationProp {
  noOfDigits?: number; // Number of OTP digits, default is 6
  hasError: string; // Error message to display
  resendTimerCount: number; // Timer count for resending OTP
  handleOtpInput: (otp: string[]) => void; // Function to handle OTP input
  triggerResendOTP: () => void; // Function to trigger resending OTP
  verifying: boolean; // Boolean to indicate if OTP is being verified
  blockResend: boolean;
  isDarkMode?: boolean;
}

// Enable layout animation for Android
if (UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

// OTPVerification component definition
const OTPVerification: React.FC<OTPVerificationProp> = ({
  noOfDigits = 6, // Default number of OTP digits is 6
  hasError,
  resendTimerCount,
  handleOtpInput,
  triggerResendOTP,
  verifying,
  blockResend,
  isDarkMode,
}) => {
  const showErrorBorder = !verifying && hasError !== '';
  const theme = ThemeProvider.getTheme();
  const colorMapping: ColorTokensMap = theme.colors;
  const [cursorPosition, setCursorPosition] = useState(1);

  const { t } = useTranslation('translation', {
    keyPrefix: 'OTP_INPUT',
  });

  const {
    otp,
    timer,
    canResend,
    showInfo,
    showHint,
    otpResent,
    focusedIndex,
    inputRefs,
    handleFocus,
    handleOtpChange,
    handleBackspace,
    handleResend,
  } = useOTPVerification({
    noOfDigits,
    resendTimerCount,
    handleOtpInput,
    triggerResendOTP,
    blockResend,
    showErrorBorder,
    cursorPosition
  });

  return (
    <>
      <View style={[ls.otpContainer, gs.setMarginLeft(-5)]}>
        {otp.map((digit, index) => {
          return (
            <TextInput
              editable={!blockResend}
              maxLength={index === noOfDigits - 1 ? 1 : undefined}
              textContentType='oneTimeCode'
              key={index}
              ref={ref => (inputRefs.current[index] = ref)}
              style={[
                ls.otpInput,
                { borderColor: index === focusedIndex ? colorMapping[ColorTokens.Primary_100]: '#ccc' },
                { color: isDarkMode ? '#fff' : '#000' },
              ]}
              keyboardType="numeric"
              value={digit}
              onChangeText={text => {
                if(!verifying) {
                  handleOtpChange(text, index)
                }
              }}
              onKeyPress={event => handleBackspace(index, event)}
              textAlign="center"
              onFocus={(e) => {
                handleFocus(index)
              }}
              onSelectionChange={(e) => {
                setCursorPosition(e?.nativeEvent.selection.start >= 1 ? 1 : 0)
              }}
            />
          );
        })}
      </View>
      <View style={[gs.setHeight(20), gs.setMarginTop(6)]}>
        {verifying && (
          <FadeInAnimation delay={100} duration={1000}>
            <View style={[gs.flexRow]}>
              <ActivityIndicator color={checkColor('#1434CB')} size={'small'} />
              <Text style={[classes.b1r, gs.setColor(colorMapping[ColorTokens.Primary_100])]}>
                {' '}
                {t('verifyingOtp')}
              </Text>
            </View>
          </FadeInAnimation>
        )}
        {!verifying && hasError && (
          <FadeInAnimation delay={100} duration={1000}>
            <Text style={[classes.b1r, gs.setColor('rgb(240, 68, 56)')]}>
              {hasError}
            </Text>
          </FadeInAnimation>
        )}
      </View>

      <View style={[gs.flexRow, gs.setMarginTop(16), gs.setMarginBottom(10)]}>
        <Typography
          fontFamily={FontFamilyTokens.Inter}
          fontSize={FontSizeTokens.SM}
          fontWeight={'400'}
          lineHeight={20}
          color={isDarkMode ? ColorTokens.White : ColorTokens.Grey_Charcoal}
          label={t('didntReceive') + '  '}
        />
        <ThemedTouchableOpacity eventName={(canResend && !blockResend) ? 'RESEND_OTP_BUTTON' : undefined} activeOpacity={(canResend && !blockResend) ? 0 : 1} style={[gs.flexRow]} onPress={handleResend}>
          <Typography
            id="txt_loginScreen_resendOtp"
            fontFamily={FontFamilyTokens.Inter}
            fontSize={FontSizeTokens.SM}
            fontWeight={'600'}
            lineHeight={20}
            color={
              canResend && !blockResend
                ? ColorTokens.Primary_100
                : isDarkMode
                  ? ColorTokens.White
                  : ColorTokens.Grey_Charcoal
            }
            label={t('resendCta')}
          />
        </ThemedTouchableOpacity>
      </View>
      <View style={{ height: 18 }}>
        {showHint && otpResent && !showInfo && resendTimerCount - timer < 6 && (
          <FadeInAnimation delay={100} duration={1000}>
            <Typography
              label={t('otpResent')}
              fontSize={FontSizeTokens.SM}
              fontFamily={FontFamilyTokens.Inter}
              lineHeight={20}
              color={ColorTokens.Secondary_100}
            />
          </FadeInAnimation>
        )}
        {showInfo && (
          <FadeInAnimation delay={100} duration={1000}>
            <Typography
              label={t('timeRemaining' , {timer : timer})}
              fontSize={FontSizeTokens.SM}
              fontFamily={FontFamilyTokens.Inter}
              lineHeight={20}
              color={isDarkMode ? ColorTokens.White : ColorTokens.Grey_Night}
            />
          </FadeInAnimation>
        )}
      </View>
    </>
  );
};

export default memo(OTPVerification);
